import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import { axInst } from "../../../Components/Login/Service";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { Loader } from "rsuite";

/* ---------------- API CALL ---------------- */
const fetchRealization = async ({ filters, selOps, viewType }) => {
  const url =
    viewType === "day"
      ? "realization/day_wise_realization"
      : "realization/month_wise_realization";

  const res = await axInst.post(url, {
    ...filters,
    ...selOps,
  });

  return res.data;
};

/* ---------------- FORMATTERS ---------------- */
const formatShortValue = (value, selOps) => {
  if (value === null || value === undefined) return "";

  const abs = Math.abs(value);
  let formatted;

  if (abs >= 1e7) formatted = (value / 1e7).toFixed(1) + " Cr";
  else if (abs >= 1e5) formatted = (value / 1e5).toFixed(1) + " L";
  else if (abs >= 1e3) formatted = (value / 1e3).toFixed(1) + " K";
  else formatted = value;

  if (selOps.retail_status === "MOP" || selOps.retail_status === "Discount")
    return `₹${formatted}`;
  return `${formatted}%`;
};

const formatExactValue = (value, selOps) => {
  if (value === null || value === undefined) return "";

  const formatted = Number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });

  if (selOps.retail_status === "MOP" || selOps.retail_status === "Discount")
    return `₹${formatted}`;
  return `${formatted}%`;
};

/* ---------------- TRANSFORMER ---------------- */
const transformData = (apiData, viewType, selOps) => {
  if (!apiData) return { categories: [], series: [] };

  // Helper to map "01" -> "January" etc.
  const monthNames = {
    "01": "January",
    1: "January",
    "02": "February",
    2: "February",
    "03": "March",
    3: "March",
    "04": "April",
    4: "April",
    "05": "May",
    5: "May",
    "06": "June",
    6: "June",
    "07": "July",
    7: "July",
    "08": "August",
    8: "August",
    "09": "September",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
  };

  if (viewType === "day") {
    // Expecting API data like: { "01-01": 84.53, "02-01": 84.83, ... }
    const entries = Object.entries(apiData || {});

    const parsed = entries
      .map(([k, v]) => {
        const [dd, mm] = k.split("-"); // "01-01" -> ["01","01"]
        const day = String(dd).padStart(2, "0");
        const month = String(mm).padStart(2, "0");
        return { day, month, value: v };
      })
      .sort((a, b) => Number(a.day) - Number(b.day)); // Keep days in order

    // Derive month name from first entry (assumes dataset is for one month)
    const monthKey = parsed[0]?.month || "01";
    const monthName = monthNames[monthKey] || "January";

    return {
      // Show only day on X-axis: "01", "02", ...
      categories: parsed.map((r) => r.day),

      series: [
        {
          // Legend series name -> "January Day Wise Comparison"
          name: `${monthName} Day Wise Comparison`,
          type: "bar",
          barWidth: 20,
          barMinHeight: 50,
          data: parsed.map((r) => r.value),
          itemStyle: {
            borderRadius: [5, 5, 0, 0],
            color: "#E0AABA",
          },
          label: {
            show: true,
            position: "inside",
            rotate: 90,
            color: "#000",
            formatter: ({ value }) => formatShortValue(value, selOps),
          },
        },
      ],
    };
  }

  // ---- Month view (unchanged) ----
  const years = Object.keys(apiData);
  const monthsOrder = [
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
    "Jan",
    "Feb",
    "Mar",
  ];

  const months = monthsOrder.filter((m) =>
    years.some((y) => apiData[y]?.[m] !== undefined),
  );

  const series = years.map((year, idx) => ({
    name: year,
    type: "bar",
    barWidth: 20,
    barMinHeight: 50,
    data: months.map((m) => apiData[year]?.[m] ?? null),
    itemStyle: {
      borderRadius: [5, 5, 0, 0],
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: idx % 2 === 0 ? "#FFEF5E" : "#68DBF2" },
        { offset: 1, color: idx % 2 === 0 ? "#F7936F" : "#509CF5" },
      ]),
    },
    label: {
      show: true,
      position: "inside",
      rotate: 90,
      color: "#000",
      formatter: ({ value }) => formatShortValue(value, selOps),
    },
  }));

  return { categories: months, series };
};

/* ---------------- COMPONENT ---------------- */
export const BarRealization = () => {
  const { data: filters, submitted } = useSelector((e) => e.filtersRealization);

  const [selOps, setSelOps] = useState({ retail_status: "Realization" });
  const [viewType, setViewType] = useState("day");

  /* Mobile / Desktop DataZoom */
  const [dataZoomEnabled, setDataZoomEnabled] = useState(true);
  const [dataZoomEnd, setDataZoomEnd] = useState(100);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width > 640) {
        setDataZoomEnabled(false);
        setDataZoomEnd(100);
      } else {
        setDataZoomEnabled(true);
        setDataZoomEnd(30);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* Fetch API */
  const { data, isFetching, isError } = useQuery({
    queryKey: ["realization-bar", submitted, selOps.retail_status, viewType],
    queryFn: () =>
      fetchRealization({
        filters,
        selOps,
        viewType,
      }),
    refetchInterval: 300000,
    cacheTime: 0,
    staleTime: 0,
    refetchIntervalInBackground: true,
  });

  const { categories, series } = useMemo(
    () => transformData(data, viewType, selOps),
    [data, viewType, selOps],
  );

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },

      formatter: (params) => {
        if (!params || !params.length) return "";

        // X-axis label (day OR month depending on view)
        const axisLabel = params[0].axisValueLabel;

        // Dropdown label
        const metricLabel =
          selOps.retail_status === "Realization"
            ? "Realization"
            : selOps.retail_status === "MOP"
              ? "MOP"
              : "Discount";

        // Value formatting
        const value = formatExactValue(params[0].value, selOps);

        return viewType === "day"
          ? `
        <b>Day: ${axisLabel}</b><br/>
        <b>${metricLabel}</b> : ${value}
      `
          : `
        <b>Month: ${axisLabel}</b><br/>
        <b>${metricLabel}</b> : ${value}
      `;
      },
    },

    legend: {
      show: true,
      top: "5px",
      left: "center",
      textStyle: { fontSize: 12, color: "#545454" },
    },
    grid: {
      top: "25%",
      left: "-3%",
      right: "3%",
      bottom: "8%",
      containLabel: true,
    },
    dataZoom: [
      {
        type: "slider",
        show: dataZoomEnabled,
        xAxisIndex: 0,
        start: 0,
        end: dataZoomEnd,
        height: 11,
        bottom: 0,
        showDetail: false,
        zoomLock: true,
        zoomOnMouseWheel: false,
        moveOnMouseMove: false,
        moveOnMouseWheel: false,
        fillerColor: "#2A5775",
        backgroundColor: "#E5E7EB",
        borderRadius: 8,
        handleSize: 12,
        handleStyle: {
          color: "#2A5775",
          borderRadius: 8,
        },
        moveHandleSize: 0,
        showDataShadow: false,
      },
    ],
    xAxis: { type: "category", data: categories, axisLabel: { interval: 0 } },
    yAxis: { type: "value", show: false, splitLine: { show: false } },
    series,
  };

  return (
    <div className="bg-white rounded-md p-3">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between gap-2">
        <h3 className="text-sm md:text-base font-semibold text-[#545454]">
          Realization, MOP &amp; Discount – Daily &amp; Monthly (CY-PY)
          Comparison
        </h3>

        <div className="flex items-center gap-2 justify-end">
          <select
            value={selOps.retail_status}
            onChange={(e) => setSelOps({ retail_status: e.target.value })}
            className="border border-gray-300 text-xs md:text-sm rounded-md h-7 w-28"
          >
            <option value="Realization">Realization(%)</option>
            <option value="MOP">MOP (₹)</option>
            <option title="Total_Discount / Retail_Quantity" value="Discount">
              Discount (₹)
            </option>
          </select>

          <div className="flex items-center gap-1">
            <span className="text-[11px] md:text-sm">
              {viewType === "day" ? "Month Wise" : "Day Wise"}
            </span>
            <label className="relative inline-block w-10 h-6">
              <input
                type="checkbox"
                checked={viewType === "month"}
                onChange={() =>
                  setViewType((p) => (p === "day" ? "month" : "day"))
                }
                className="sr-only"
              />
              <span
                className={`block h-6 rounded-full ${
                  viewType === "month" ? "bg-[#AAE0D3]" : "bg-[#b9b8f4]"
                }`}
              >
                <span
                  className={`block h-5 w-5 rounded-full shadow-md transform transition ${
                    viewType === "month"
                      ? "translate-x-4 bg-[#599f8e]"
                      : "bg-[#8381fe]"
                  } translate-y-0.5`}
                />
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* BODY */}
      {isFetching && (
        <div className="flex justify-center py-6">
          <Loader />
        </div>
      )}

      {isError && <p className="text-red-500">Error loading data</p>}

      {!isFetching && data && (
        <ReactECharts option={option} style={{ height: 150, width: "100%" }} />
      )}
    </div>
  );
};
